import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Search, Waypoints, ShieldQuestion } from 'lucide-react';
import { walletIntelApi } from '@/api/walletIntel';
import { CATEGORY_LABEL, CATEGORY_COLOR, CATEGORY_EXPLANATION, SOURCE_ADMIRALTY_GRADE } from '@/types/walletIntel';
import { ClusterGraph } from './ClusterGraph';
import { AdmiraltyBadge } from './AdmiraltyBadge';

function shorten(addr: string): string {
  if (addr.length <= 20) return addr;
  return `${addr.slice(0, 12)}...${addr.slice(-8)}`;
}

interface Props {
  category: string;
  onClose: () => void;
}

export function CategoryExplorerModal({ category, onClose }: Props) {
  const navigate = useNavigate();
  const color = CATEGORY_COLOR[category] ?? '#5A7A9A';
  const [graphFor, setGraphFor] = useState<{ chain: string; address: string } | null>(null);

  const sampleQuery = useQuery({
    queryKey: ['wallet-intel-category-sample', category],
    queryFn: () => walletIntelApi.categorySample(category, 8),
  });

  const clusterQuery = useQuery({
    queryKey: ['wallet-intel-cluster', graphFor?.chain, graphFor?.address],
    queryFn: () => walletIntelApi.cluster({ chain: graphFor!.chain, address: graphFor!.address, max_hops: 1, max_fanout: 8 }),
    enabled: !!graphFor,
  });

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(3,5,8,0.82)', backdropFilter: 'blur(4px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          onClick={(e) => e.stopPropagation()}
          style={{ background: '#090D13', border: `1px solid ${color}40`, boxShadow: `0 0 60px ${color}18`, borderRadius: 18, width: '100%', maxWidth: 760, maxHeight: '86vh', overflowY: 'auto' }}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 px-7 pt-6 pb-5" style={{ borderBottom: '1px solid #182030' }}>
            <div className="flex items-center gap-4">
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `${color}20`, border: `1.5px solid ${color}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ShieldQuestion size={24} style={{ color }} />
              </div>
              <div>
                <h2 style={{ color: '#F2F6FC', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 22 }}>
                  {CATEGORY_LABEL[category] ?? category}
                </h2>
                <p style={{ color: '#8D96AC', fontSize: 13, marginTop: 4, lineHeight: 1.6, maxWidth: 520 }}>
                  {CATEGORY_EXPLANATION[category] ?? 'No description available for this category yet.'}
                </p>
              </div>
            </div>
            <button onClick={onClose} style={{ color: '#5A7A9A' }} className="flex-shrink-0 hover:text-white transition-colors">
              <X size={20} />
            </button>
          </div>

          {/* Sample addresses */}
          <div className="px-7 py-6">
            <p style={{ color: '#5A7A9A', fontSize: 11, letterSpacing: '0.1em', marginBottom: 14, fontWeight: 700 }}>
              REAL EXAMPLES FROM THIS CATEGORY
            </p>

            {sampleQuery.isLoading ? (
              <div className="flex items-center justify-center py-14"><Loader2 className="animate-spin" size={22} style={{ color }} /></div>
            ) : sampleQuery.isError ? (
              <p style={{ color: '#FF1F1F', fontSize: 12 }}>Failed to load examples for this category.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {(sampleQuery.data?.examples ?? []).map((ex, i) => (
                  <motion.div
                    key={`${ex.address}-${i}`}
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: i * 0.04 }}
                    style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: '14px 16px' }}
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                      <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 13, fontWeight: 700, wordBreak: 'break-all' }}>
                        {shorten(ex.address)}
                      </span>
                      <span style={{ color: '#5A7A9A', fontSize: 11 }}>{ex.chain}</span>
                    </div>
                    <p style={{ color: '#8D96AC', fontSize: 12.5, lineHeight: 1.5, marginBottom: 10 }}>{ex.citation}</p>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <AdmiraltyBadge grade={SOURCE_ADMIRALTY_GRADE[ex.source]} compact />
                        <span style={{ color: '#5A7A9A', fontSize: 11 }}>{ex.source_date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setGraphFor({ chain: ex.chain, address: ex.address })}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium text-[11px]"
                          style={{ background: '#0F2030', color: '#00C8FF', border: '1px solid #1E3A5F' }}
                        >
                          <Waypoints size={12} /> View relationships
                        </button>
                        <button
                          onClick={() => navigate(`/app/wallet-intel/lookup?address=${encodeURIComponent(ex.address)}&chain=${ex.chain}`)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium text-[11px]"
                          style={{ background: '#0C1118', color: '#8D96AC', border: '1px solid #182030' }}
                        >
                          <Search size={12} /> Full profile
                        </button>
                      </div>
                    </div>

                    {/* Inline relationship graph, opt-in per address so we don't fire
                        8 Breadcrumbs calls just for opening this modal */}
                    <AnimatePresence>
                      {graphFor?.address === ex.address && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                          style={{ overflow: 'hidden', marginTop: 12 }}
                        >
                          {clusterQuery.isLoading ? (
                            <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin" size={18} style={{ color: '#00C8FF' }} /></div>
                          ) : clusterQuery.isError ? (
                            <p style={{ color: '#FF1F1F', fontSize: 11 }}>Failed to load relationships for this address.</p>
                          ) : clusterQuery.data ? (
                            <ClusterGraph cluster={clusterQuery.data} chain={ex.chain} />
                          ) : null}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
                {(sampleQuery.data?.examples.length ?? 0) === 0 && (
                  <p style={{ color: '#3A5A7A', fontSize: 12 }}>No examples on file for this category.</p>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
